import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLoaderData, useRevalidator } from "react-router";
import {
  Tab,
  Tabs,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import {
  ClientInfoCard,
  RiskAllergyCard,
  AppointmentHistoryCard,
  NextAppointmentCard,
  NextAppointmentNoteCard,
} from "../components/client/cards";
import ClientAvatar from "../components/client/ClientAvatar";
import AppointmentWizard from "../components/client/AppointmentWizard";
import AppointmentNotesTabContent from "../components/client/AppointmentNotesTabContent";
import AppointmentNoteFields from "../components/client/AppointmentNotesFields";
import {
  WizardProvider,
  useWizardContext,
} from "../components/client/AppointmentWizardContext";
import {
  useWizardSteps,
  WIZARD_STEPS,
} from "../components/client/hooks/useWizardSteps";
import * as api from "../services/api";
import {
  appointmentAdhocType,
  appointmentStep,
  status,
} from "shared/src/appointment.mjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faCircleInfo as faCircleInfoFull,
} from "@fortawesome/free-solid-svg-icons";
import { faClipboardUser } from "@fortawesome/pro-solid-svg-icons";
import { faCircleInfo, faXmark } from "@fortawesome/pro-light-svg-icons";
import { fullName } from "../utils/formatter";
import { currentLocationFromRequest } from "../components/LocationSelector";
import { useRoles } from "../context/AuthenticationTokenContext";
import { role, isGrantedAny } from "shared/src/roles.mjs";
import ConfirmationDialog from "../components/ConfirmationDialog.jsx";
import AppointmentTimer from "../components/client/AppointmentTimer.jsx";
import * as d from "shared/src/date.mjs";
import { Gallery } from "../components/client/Gallery";
import { useSnackbarMessage } from "../components/Snackbar.jsx";
import { indexBy } from "shared/src/util.mjs";
import { Documents } from "../components/client/Documents";
import { PrefCard } from "../components/client/cards/PrefCard";
import { ContactsCard } from "../components/client/cards/ContactsCard";
import { PaymentCard } from "../components/client/cards/PaymentCard";
import { TreatmentCard } from "../components/client/cards/TreatmentCard";
import { useNavigate } from "../url.js";
import { logout } from "../auth";

export async function loader({ request, params }) {
  const { id: clientId, appointmentId } = params;
  const locationId = currentLocationFromRequest(request);

  const now = new Date();
  const startOfDay = d.startOfDay(now);
  const endOfDay = d.endOfDay(now);

  const [appointmentDataRes, activeNotesRes] = await Promise.all([
    api.loadAppointmentClientData({
      clientId,
      locationId,
      startOfDay: d.toString(startOfDay),
      endOfDay: d.toString(endOfDay),
      now: d.toString(now),
      nameDaysFilter: {
        month: { eq: now.getMonth() + 1 },
        day: { eq: now.getDate() },
      },
      doctorFilter: { roles: { location_id: locationId, role: role.Doctor } },
    }),
    api.nextAppointmentNotes({
      client_id: { eq: clientId },
      active: { eq: true },
    }),
  ]);

  const todaysAppointments = appointmentDataRes.data?.appointments?.data || [];
  const currentAppointment = todaysAppointments.find(
    (a) => a.id === appointmentId,
  );

  if (!currentAppointment || !currentAppointment.client) {
    throw new Response("Client Not Found", { status: 404 });
  }

  const rawClient = currentAppointment.client || {};
  const client = {
    ...rawClient,
    pending_changes: rawClient.pending_changes || {},
  };

  const nextAppointmentNotes = (
    activeNotesRes?.data?.nextAppointmentNotes?.data ?? []
  )
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return {
    client: {
      ...client,
      todaysAppointments,
      currentAppointmentId: appointmentId,
    },
    legalGuardians: [],
    nextAppointmentNotes,
    nameDayNames: new Set(
      (appointmentDataRes.data?.nameDays?.data || []).map((d) => d.name),
    ),
    doctorById: indexBy(
      appointmentDataRes.data?.doctors?.data || [],
      (d) => d.id,
    ),
    lastAppointment:
      appointmentDataRes?.data?.lastAppointments?.data?.[0] || null,
    hasBraceProduct: (appointmentDataRes?.data?.braceProducts?.count ?? 0) > 0,
  };
}

function ClientAppointmentContent() {
  const { t } = useTranslation();
  const loaderData = useLoaderData();
  const {
    client,
    nextAppointmentNotes,
    nameDayNames,
    lastAppointment,
    hasBraceProduct,
  } = loaderData;

  const todaysAppointments = client.todaysAppointments || [];
  const appointmentId = client.currentAppointmentId;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const displaySnackbarMessage = useSnackbarMessage();

  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "1";
  });
  const {
    setClientData,
    rtgCreated,
    displayFullInfo,
    setDisplayFullInfo,
    setNextAppointmentNote,
    setIsRtgMandatoryForCurrentAppointment,
  } = useWizardContext();
  const { activeStep } = useWizardSteps();
  const [rtgDialogOpen, setRtgDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [fullInfoDialogOpen, setFullInfoDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const hasPrefilledNoteRef = React.useRef(false);

  function pickInitialNextApptNote(notes = []) {
    const da = notes
      .filter((n) => n?.role === "Dental_Assistant")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    return da?.note || "";
  }

  const currentAppointment = useMemo(
    () => todaysAppointments.find((a) => a.id === appointmentId),
    [todaysAppointments, appointmentId],
  );

  const isAppointmentFinished =
    currentAppointment?.status_id === status.FINISHED;
  const isAppointmentStarted = activeStep > 0;
  const showNotesTab =
    activeStep >= WIZARD_STEPS.RECEPTION_NOTES && !isAppointmentFinished;

  const isRTGAppointment = useMemo(() => {
    return currentAppointment?.service?.type === "rentgen";
  }, [currentAppointment]);

  const isRtgMandatoryForCurrentAppointment = React.useMemo(() => {
    const requires =
      currentAppointment?.service?.locationService?.requires_rentgen === true;
    const types =
      currentAppointment?.service?.locationService?.actual_calendar?.types;
    return Boolean(requires && Array.isArray(types) && types.includes("other"));
  }, [currentAppointment]);

  const isFirstConsultationAppointment = useMemo(() => {
    const type = currentAppointment?.service?.type;
    return type === "consultation";
  }, [currentAppointment]);

  const showActionButtons =
    (activeStep === WIZARD_STEPS.COMPLETION ||
      activeStep === WIZARD_STEPS.RECEPTION_NOTES) &&
    !isAppointmentFinished;

  const showSendToRTGButton =
    activeStep >= WIZARD_STEPS.COMPLETION &&
    activeStep !== WIZARD_STEPS.CHAIR_CLEANING &&
    activeStep !== WIZARD_STEPS.PROCEDURE_NOTES &&
    !isRTGAppointment &&
    !isFirstConsultationAppointment &&
    !rtgCreated &&
    !isAppointmentFinished &&
    hasBraceProduct;

  const roles = useRoles();

  const isNurseOrFloorManager = [
    role.Dental_Assistant,
    role.Floor_Manager,
  ].some((r) => roles.has(r));

  const displayMinimalInfo =
    isNurseOrFloorManager &&
    !isAppointmentFinished &&
    activeStep !== WIZARD_STEPS.PREPARATION &&
    !displayFullInfo;

  const canSeeAppointmentHistoryTab = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeNextAppointmentNoteCard = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeContactsCard = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeProblemCard = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  const canSeePaymentCard = isGrantedAny(
    roles,
    new Set([
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const handleTabChange = useCallback((_, newValue) => {
    setTabValue(newValue);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", newValue);
    window.history.replaceState({}, "", url.toString());
  }, []);

  const handleSendToRTG = async () => {
    try {
      const { data } = await api.getServiceByName("Rentgen");
      const rentgenServiceId = data?.serviceByName?.id;
      if (!client?.id || !rentgenServiceId) return;

      const params = new URLSearchParams(window.location.search);
      const location_id = params.get("g_location");
      const now = new Date();
      const duration = 20;
      const start = now.toISOString();
      const end = new Date(now.getTime() + duration * 60000).toISOString();

      const rtgAppointment = {
        client_ids: [client.id],
        service_id: rentgenServiceId,
        location_id,
        status_id: status.WAITING,
        adhoc_type: appointmentAdhocType.CHAIR,
        start,
        end,
      };
      const createRes = await api.createAppointment(rtgAppointment);

      displaySnackbarMessage({
        title: "RTG appointment vytvořen!",
        content: "",
      });
      setClientData((prev) => {
        const list = Array.isArray(prev?.todaysAppointments)
          ? prev.todaysAppointments.slice()
          : [];
        list.push({
          id: createRes?.data?.createAppointment?.id || `rtg-opt-${Date.now()}`,
          status_id: status.WAITING,
          service: { type: "rentgen", name: "RTG" },
        });
        return { ...prev, todaysAppointments: list };
      });

      revalidator.revalidate();
      setRtgDialogOpen(false);
    } catch (error) {
      console.error("RTG Error:", error);
      displaySnackbarMessage({
        title: "Chyba při vytváření RTG appointmentu!",
        severity: "error",
        content: "",
      });
    }
  };

  useEffect(() => {
    setIsRtgMandatoryForCurrentAppointment(isRtgMandatoryForCurrentAppointment);
  }, [
    isRtgMandatoryForCurrentAppointment,
    setIsRtgMandatoryForCurrentAppointment,
  ]);

  useEffect(() => {
    if (client?.id) {
      setClientData({
        id: client.id,
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        birth_number: client.birth_number,
        phone: client.phone,
        street: client.street,
        city: client.city,
        post_code: client.post_code,
        birthdate: client.birthdate,
        location_id: client.location_id,
        todaysAppointments,
        currentAppointmentId: client.currentAppointmentId,
      });

      if (!hasPrefilledNoteRef.current) {
        const initial = pickInitialNextApptNote(nextAppointmentNotes);
        if (initial) {
          setNextAppointmentNote(initial);
        }
        hasPrefilledNoteRef.current = true;
      }
    }

    const timer = setTimeout(() => setLoading(false), 300);
    return () => {
      clearTimeout(timer);
    };
  }, [client, setClientData, todaysAppointments, setNextAppointmentNote]);

  useEffect(() => {
    if (activeStep >= WIZARD_STEPS.RECEPTION_NOTES && !isAppointmentFinished) {
      setTabValue("4");
    } else {
      setTabValue("1");
    }
  }, [activeStep, isAppointmentFinished]);

  useEffect(() => {
    if (currentAppointment?.status_id === status.FINISHED) {
      logout({ navigate });
    }
  }, [currentAppointment.status_id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: "64px",
          backgroundColor: "background.default",
          zIndex: 10,
        }}
      >
        <ClientAvatar
          client={client}
          t={t}
          nameDayNames={nameDayNames}
          displayMinimalInfo={displayMinimalInfo}
          additionalContent={
            <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
              {isNurseOrFloorManager &&
                activeStep !== WIZARD_STEPS.PREPARATION && (
                  <Tooltip
                    title={`${displayFullInfo ? "Mini" : "Plná"} karta`}
                    arrow
                  >
                    <IconButton onClick={() => setFullInfoDialogOpen(true)}>
                      <FontAwesomeIcon
                        icon={displayFullInfo ? faCircleInfoFull : faCircleInfo}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              <Typography variant="h2" sx={{ ml: 10, fontWeight: 600 }}>
                Aktuální termín: {currentAppointment?.service?.name || "—"}
              </Typography>
            </Box>
          }
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
            gap: 2,
          }}
        >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t("client.tabs.overview")} value="1" />
            {!displayMinimalInfo && canSeeAppointmentHistoryTab && (
              <Tab label={t("client.tabs.appointmentHistory")} value="2" />
            )}
            {!isNurseOrFloorManager && (
              <Tab label={t("client.tabs.documents")} value="3" />
            )}
            <Tab label={t("client.tabs.gallery")} value="5" />
            {showNotesTab && (
              <Tab label={t("client.tabs.appointmentNotes")} value="4" />
            )}
          </Tabs>
          <Box sx={{ display: "flex", gap: 2 }}>
            {showSendToRTGButton && (
              <Button
                onClick={() => setRtgDialogOpen(true)}
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faClipboardUser} />}
              >
                {t("client.sendToRTG")}
              </Button>
            )}
            {showActionButtons && (
              <Button
                onClick={() => setNotesDialogOpen(true)}
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faCalendarDays} />}
              >
                {t("client.writeNotes")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <TabContext value={tabValue}>
        <TabPanel value="1" sx={{ paddingBottom: "150px" }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={6}>
              <ClientInfoCard
                client={client}
                displayMinimalInfo={displayMinimalInfo}
                lastAppointment={lastAppointment}
              />
            </Grid>

            {canSeePaymentCard && (
              <Grid size={3}>
                <PaymentCard client={client} />
              </Grid>
            )}

            {canSeeContactsCard && (
              <Grid size={3}>
                <ContactsCard client={client} />
              </Grid>
            )}
            {isAppointmentStarted && !isAppointmentFinished && (
              <Grid
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <AppointmentTimer
                  duration={
                    currentAppointment.service.locationService.real_duration
                  }
                  startTime={
                    d.parseUtcDateTime(
                      currentAppointment.audit.data.findLast(
                        (i) =>
                          i.changed_fields?.active_step ===
                          appointmentStep.COMPLETION,
                      )?.created_at,
                    ) ?? null
                  }
                />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            {!displayMinimalInfo && (
              <Grid size={3}>
                <NextAppointmentCard appointments={todaysAppointments} />
              </Grid>
            )}

            {!displayMinimalInfo && canSeeNextAppointmentNoteCard && (
              <Grid size={3}>
                <NextAppointmentNoteCard notes={nextAppointmentNotes} />
              </Grid>
            )}

            <Grid size={3}>
              <RiskAllergyCard client={client} />
            </Grid>

            {!displayMinimalInfo && canSeeProblemCard && (
              <Grid size={3}>
                <PrefCard client={client} />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  my: 2,
                }}
              >
                Produkty
              </Typography>
              <TreatmentCard
                client={client}
                displayMinimalInfo={displayMinimalInfo}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {canSeeAppointmentHistoryTab && (
          <TabPanel value="2">
            <AppointmentHistoryCard clientId={client.id} />
          </TabPanel>
        )}

        {!isNurseOrFloorManager && (
          <TabPanel value="3">
            <Documents client_id={client.id} />
          </TabPanel>
        )}

        {showNotesTab && tabValue === "4" && (
          <TabPanel value="4">
            <AppointmentNotesTabContent />
          </TabPanel>
        )}

        {tabValue === "5" && (
          <TabPanel value="5">
            <Gallery client_id={client.id} />
          </TabPanel>
        )}
      </TabContext>

      {currentAppointment?.id && <AppointmentWizard />}

      <Dialog open={rtgDialogOpen} onClose={() => setRtgDialogOpen(false)}>
        <DialogTitle>
          {t("Poslat na RTG")}
          <IconButton
            aria-label="close"
            onClick={() => setRtgDialogOpen(false)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {t("rtg.confirmationMessage", { name: fullName(client) })}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRtgDialogOpen(false)}>
            {t("Zrušit")}
          </Button>
          <Button variant="contained" onClick={handleSendToRTG}>
            {t("Potvrdit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setNotesDialogOpen(false)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AppointmentNoteFields />
        </DialogContent>
      </Dialog>

      {fullInfoDialogOpen && (
        <ConfirmationDialog
          title={`Přepnutí do ${displayFullInfo ? "mini" : "plné"} karty`}
          content={`Opravdu se chcete přepnout do ${displayFullInfo ? "mini" : "plné"} karty`}
          onDecision={async (decision) => {
            setFullInfoDialogOpen(false);
            if (decision) {
              if (displayFullInfo) setTabValue("1");
              setDisplayFullInfo(!displayFullInfo);
            }
          }}
        />
      )}
    </>
  );
}

export default function ClientAppointment() {
  return (
    <WizardProvider>
      <ClientAppointmentContent />
    </WizardProvider>
  );
}
